var BookmarkButton   = require("app/Bookmarks/BookmarkButton"),
    Drawer           = require("enyo-ui/Drawer"),
    HeaderVariables  = require("app/theme/styles/headers/HeaderVariables"),
    HyperLinkSupport = require("enyo-ui/HyperLinkSupport"),
    IconButton       = require("app/ui/IconButton"),
    Layout           = require("./VerticalDescriptionLayout"),
    MetadataList     = require("./MetadataList"),
    PlayMediaButton  = require("app/MediaPlayer/PlayMediaButton"),
    SignUpButton     = require("app/Auth/SignUpButton"),
    SvgIcon          = require("app/ui/SvgIcon"),
    clipText         = require("app/utils/clipText"),
    frameThrottle    = require("enyo-perf/utils").frameThrottle,
    get              = require("object-path").get,
    humanizeTimespan = require("app/utils/humanizeTimespan"),
    icons            = require("app/theme/icons"),
    kind             = require("enyo/kind"),
    onHyperlinkTap   = require("app/utils/onHyperlinkTap"),
    utils            = require("enyo/utils");

require("./VerticalDescription.less");
require("app/theme/IconButton--neutral-ghost.less");
require("app/theme/IconButton--neutral.less");
require("app/theme/IconButton--positive.less");
require("app/theme/IconButton--read-more.less");

module.exports = kind(/** @lends  app.ui.header.VerticalDescription.prototype */ {

    /**
     * @private
     */
    name: "app.ui.header.VerticalDescription",

    /**
     * @private
     */
    classes: "app-ui-header-vertical-description",

    /**
     * @private
     */
    layoutKind: Layout,

    /**
     * The dataset
     *
     * @type  {?Object}
     * @public
     */
    data: null,

    /**
     * @private
     */
    components: [{
        name:       "titleWrapper",
        tag:        "h1",
        classes:    "app-ui-header-vertical-description__title-wrapper",
        components: [{
            name:       "liveBadge",
            tag:        "span",
            classes:    "app-ui-header-vertical-description__live-badge",
            showing:    false,
            components: [{
                name:    "liveBadgeIcon",
                kind:    SvgIcon,
                classes: "app-ui-header-vertical-description__live-badge-icon",
                icon:    icons["app.ui.header.VerticalDescription.liveIcon"]
            }, {
                name:    "liveBadgeCaption",
                classes: "app-ui-header-vertical-description__live-badge-caption"
            }]
        }, {
            name:    "title",
            tag:     "span",
            classes: "app-ui-header-vertical-description__title"
        }]
    }, {
        name:    "secondaryTitle",
        tag:     "h2",
        classes: "app-ui-header-vertical-description__secondary-title"
    }, {
        name:       "buttonBar",
        classes:    "app-ui-header-vertical-description__button-bar",
        components: [{
            name:    "playMediaButton",
            kind:    PlayMediaButton,
            classes: "app-ui-header-vertical-description__play-media-button",
            theme:   "neutral"
        }, {
            name:    "signUpButton",
            kind:    SignUpButton,
            classes: "app-ui-header-vertical-description__sign-up-button",
            theme:   "neutral-ghost"
        }, {
            name:    "bookmarkButton",
            kind:    BookmarkButton,
            classes: "app-ui-header-vertical-description__bookmark-button"
        }]
    }, {
        name:       "bodyDrawer",
        kind:       Drawer,
        classes:    "app-ui-header-vertical-description__body-drawer",
        components: [{
            name:      "body",
            allowHtml: true,
            classes:   "app-ui-header-vertical-description__body"
        }]
    }, {
        name:          "readMore",
        kind:          IconButton,
        classes:       "app-ui-header-vertical-description__read-more",
        ontap:         "toggleReadMore",
        theme:         "read-more",
        iconAlignment: "right"
    }, {
        name:    "metadata",
        kind:    MetadataList,
        classes: "app-ui-header-vertical-description__metadata"
    }, {
        name:       "ctaBar",
        classes:    "app-ui-header-vertical-description__cta-bar",
        components: [{
            name:    "ctaButton",
            kind:    IconButton,
            classes: "app-ui-header-vertical-description__cta-button",
            mixins:  [HyperLinkSupport],
            ontap:   "onHyperlinkTap",
            theme:   "positive",
            icon:    icons["app.ui.header.VerticalDescription.ctaButton"]
        }]
    }],

    /**
     * @private
     */
    observers: {
        updateAndResize: ["data"]
    },

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            var $bodyDrawer;

            this.updateAndResize    = frameThrottle(this.bindSafely(this.updateAndResize));
            this.lazyResize         = frameThrottle(this.bindSafely(this.resize));
            this.onBodyDrawerClosed = this.bindSafely(this.onBodyDrawerClosed);
            sup.apply(this, arguments);
            $bodyDrawer             = this.$.bodyDrawer;
            $bodyDrawer.on("closed", this.onBodyDrawerClosed);
            this.observe("app.$.i18nService.translation", this.updateAndResize);
            this.observe("app.$.authService.isLoggedIn", this.updateAndResize);

            /**
             * @important Observe this property straight from the bookmark
             * button.
             *
             * The observer style usually applied in this application would be:
             *
             * ```
             * this.observe('$.bookmarkButton.showing', this.lazyResize);
             * ```
             *
             * However, this appears to fire `this.lazyResize` before the
             * bookmarkButton fires it's `showingChanged` method, which is
             * responsible for updating the button's visibility in the DOM.
             *
             * We want our layout to respond to the change in visibility though,
             * so it's important the reflow happens after the DOM has been
             * updated. Observing directly on the button instead of using an
             * observer path seems to enforce this behavior.
             */
            this.$.bookmarkButton.observe("showing", this.lazyResize);
            this.updateAndResize();
        };
    }),

    /**
     * This component needs to resize after it's been updated, but we want the
     * update function to be extensible. This method allows us to resize after
     * all updates are done.
     *
     * @private
     */
    updateAndResize: function() {
        this.update();
        this.resize();
    },

    /**
     * @protected
     */
    update: function() {
        var i18nService     = this.app.$.i18nService,
            data            = this.data || null,
            mediaType       = get(data, "type", null),
            secondaryTitle  = get(data, "meta.secondary_title", null),
            metadata        = [],
            artists         = get(data, "artists", []),
            year            = get(data, "year", null),
            $secondaryTitle = this.$.secondaryTitle,
            $signUpButton   = this.$.signUpButton,
            $bodyDrawer     = this.$.bodyDrawer,
            $readMore       = this.$.readMore,
            $metadata       = this.$.metadata,
            $ctaBar         = this.$.ctaBar,
            $ctaButton      = this.$.ctaButton;

        this.$.title.set("content", get(data, "meta.title", null) || "");

        if (secondaryTitle) {
            $secondaryTitle.set("content", secondaryTitle);
            $secondaryTitle.show();
        }
        else {
            $secondaryTitle.hide();
        }

        if (HeaderVariables.getSignUpButtonVisibility(mediaType) && $signUpButton.shouldShow()) {
            $signUpButton.show();
        }
        else {
            $signUpButton.hide();
        }

        this.$.bookmarkButton.set("mediaItem", data);

        this.$.body.set("content", get(data, "meta.html_long_description", null) || "");

        if ($bodyDrawer.isOpen || $bodyDrawer.willOpen) {
            $readMore.set("caption", i18nService.getString("app.ui.header.VerticalDescription.showLess"));
            $readMore.set("icon", icons["app.ui.header.VerticalDescription.showLess"]);
        }
        else {
            $readMore.set("caption", i18nService.getString("app.ui.header.VerticalDescription.readMore"));
            $readMore.set("icon", icons["app.ui.header.VerticalDescription.readMore"]);
        }

        if (artists.length > 0 && artists.join(", ") !== secondaryTitle) {
            if (artists.length === 1) {
                metadata.push({
                    id:    utils.uid(),
                    label: i18nService.getString("app.ui.header.MetadataListItem.artist"),
                    value: artists[0]
                });
            }
            else {
                metadata.push({
                    id:    utils.uid(),
                    label: i18nService.getString("app.ui.header.MetadataListItem.artists"),
                    value: artists.join(", ")
                });
            }
        }

        if (year !== null) {
            metadata.push({
                id:    utils.uid(),
                label: i18nService.getString("app.ui.header.MetadataListItem.year"),
                value: year
            });
        }

        $metadata.set("data", metadata);

        if (get(data, "call_to_action_enabled", false)) {
            $ctaButton.set("caption", get(data, "meta.call_to_action_text", null) || "");
            $ctaButton.set("href", get(data, "meta.call_to_action_url", null));
            $ctaBar.show();
        }
        else {
            $ctaButton.set("caption", "");
            $ctaButton.set("href", null);
            $ctaBar.hide();
        }
    },

    /**
     * Start the loop that checks the status of the livestream
     *
     * @param  {Number}  start  The start of the live-stream as Unix Timestamp
     *                          in seconds
     * @param  {Number}  end    The end of the live-stream as Unix Timestamp in
     *                          seconds
     * @public
     */
    startLivestreamStatus: function(start, end) {
        this.updateLivestreamStatus(start, end);
        this.app.$.countdownService.request(this.id + "::updateLivestreamStatus", this.bindSafely(function() {
            this.updateLivestreamStatus(start, end);
        }));
    },

    /**
     * Update the status of the livestream
     *
     * @param  {Number}  start  The start of the live-stream as Unix Timestamp
     *                          in seconds
     * @param  {Number}  end    The end of the live-stream as Unix Timestamp in
     *                          seconds
     * @public
     */
    updateLivestreamStatus: function(start, end) {
        var i18nService       = this.app.$.i18nService,
            countdownService  = this.app.$.countdownService,
            language          = i18nService.get("language"),
            now               = Date.now() / 1000,
            eta,
            humanizedEta,
            $liveBadge        = this.$.liveBadge,
            $liveBadgeIcon    = this.$.liveBadgeIcon,
            $liveBadgeCaption = this.$.liveBadgeCaption;

        // Livestream pending
        if (start > now) {
            eta          = start - now;
            humanizedEta = humanizeTimespan(eta, language, true);
            $liveBadgeIcon.hide();
            $liveBadgeCaption.set("content", i18nService.getString("app.ui.header.VerticalDescription.LiveBadge.livePendingLabel", {
                eta: humanizedEta
            }));

            if (!$liveBadge.get("showing")) {
                $liveBadge.show();
            }

            countdownService.request(this.id + "::updateLivestreamStatus", this.bindSafely(function() {
                this.updateLivestreamStatus(start, end);
            }));
        }

        // Now live streaming
        else if (start <= now && now < end) {
            $liveBadgeIcon.show();
            $liveBadgeCaption.set("content", i18nService.getString("app.ui.header.VerticalDescription.LiveBadge.liveLabel"));

            if (!$liveBadge.get("showing")) {
                $liveBadge.show();
            }

            countdownService.request(this.id + "::updateLivestreamStatus", this.bindSafely(function() {
                this.updateLivestreamStatus(start, end);
            }));
        }

        // Stream done
        else {
            this.stopLivestreamStatus();
        }
    },

    /**
     * Stop updating the livestream status
     *
     * @public
     */
    stopLivestreamStatus: function() {
        var $liveBadge        = this.$.liveBadge,
            $liveBadgeIcon    = this.$.liveBadgeIcon,
            $liveBadgeCaption = this.$.liveBadgeCaption;

        if ($liveBadge.get("showing")) {
            $liveBadge.hide();
        }
        $liveBadgeIcon.hide();
        $liveBadgeCaption.set("content", "");
        this.app.$.countdownService.cancel(this.id + "::updateLivestreamStatus");
    },

    /**
     * Toggle the bodyDrawer.
     *
     * @private
     */
    toggleReadMore: function() {
        if (!this.get("readMore")) {
            this.openReadMore(true);
            this.set("readMore", true);
        }
        else {
            this.closeReadMore(true);
            this.set("readMore", false);
        }
    },

    /**
     * @param  {boolean}  animate  Whether to animate the transition
     * @private
     */
    openReadMore: function(animate) {
        var i18nService = this.app.$.i18nService,
            bodyNode    = this.$.body.hasNode(),
            $readMore   = this.$.readMore;

        bodyNode && clipText.unclip(bodyNode);
        this.$.bodyDrawer.open(animate);
        $readMore.set("caption", i18nService.getString("app.ui.header.VerticalDescription.showLess"));
        $readMore.set("icon", icons["app.ui.header.VerticalDescription.showLess"]);
    },

    /**
     * @param  {boolean}  animate  Whether to animate the transition
     * @private
     */
    closeReadMore: function(animate) {
        var i18nService = this.app.$.i18nService,
            $readMore   = this.$.readMore;

        this.$.bodyDrawer.close(animate);
        $readMore.set("caption", i18nService.getString("app.ui.header.VerticalDescription.readMore"));
        $readMore.set("icon", icons["app.ui.header.VerticalDescription.readMore"]);
    },

    /**
     * @private
     */
    onBodyDrawerClosed: function() {
        var bodyNode = this.$.body.hasNode();

        bodyNode && clipText.clip(bodyNode, this.$.bodyDrawer.get("closedSize"), true);
    },

    /**
     * Responds to a click on a hyperlink.
     *
     * @return  {boolean}  Stop propagation
     * @private
     */
    onHyperlinkTap: onHyperlinkTap

});
